import { createApi } from '@reduxjs/toolkit/query/react';
import {
    GetSupportParams,
    GetSupportResponse,
    GetSupportStatsResponse,
    GetSupportStatsParams,
    GetSupportTicketDetailsResponse,
    GetSupportTicketHistoryResponse,
    CloseSupportTicketResponse,
    CreateChatParams,
    CreateChatResponse,
    GetSupportMemberTicketsResponse,
    GetSupportChatHistoryResponse,
    GetSupportChatHistoryParams,
} from '../../types/support';
import { baseQueryWithReauth } from './baseQuery';

export const SupportService = createApi({
    reducerPath: 'SupportService',
    baseQuery: baseQueryWithReauth,
    tagTypes: ['SupportTickets', 'SupportTicketDetails', 'SupportStats', 'SupportHistory'],
    endpoints: ({ query, mutation }) => ({

        getSupportStats: query<GetSupportStatsResponse, GetSupportStatsParams | void>({
            query: (params) => ({
                url: '/ticket-service/v1/support-tickets/stats',
                method: 'GET',
                params:
                    params && typeof params === 'object' && Object.keys(params).length > 0
                        ? {
                            statusIds: params.statusIds || params.status || undefined,
                            dateFrom: params.dateFrom || params.fromDate || undefined,
                            dateTo: params.dateTo || params.toDate || undefined,
                            customerType: params.customerType || undefined,
                        }
                        : undefined,
            }),
            providesTags: ['SupportStats'],
        }),

        getSupportTickets: query<GetSupportResponse, GetSupportParams>({
            query: (params) => ({
                url: '/ticket-service/v1/support-tickets',
                method: 'GET',
                params: {
                    page: params.page ?? 0,
                    search: params.search || undefined,
                    limit: params.limit ?? 10,
                    statusIds: params.statusIds || params.status || undefined,
                    dateFrom: params.dateFrom || params.fromDate || undefined,
                    dateTo: params.dateTo || params.toDate || undefined,
                    customerType: params.customerType || undefined,
                },
            }),
            providesTags: ['SupportTickets'],
        }),


        getSupportTicketDetails: query<GetSupportTicketDetailsResponse, { ticketId: string }>({
            query: ({ ticketId }) => ({
                url: `/ticket-service/v1/support-tickets/${ticketId}/details`,
                method: 'GET',
            }),
            providesTags: (result, error, { ticketId }) => [
                { type: 'SupportTicketDetails', id: ticketId }
            ],
        })
        ,

        getSupportTicketHistory: query<GetSupportTicketHistoryResponse, { ticketId: string }>({
            query: ({ ticketId }) => ({
                url: `/ticket-service/v1/support-tickets/${ticketId}/history`,
                method: 'GET',
            }),
            providesTags: (result, error, { ticketId }) => [
                { type: 'SupportHistory', id: ticketId }
            ],
        })

        ,
        assignSupportTicket: mutation<CloseSupportTicketResponse, { id: string }>({
            query: ({ id }) => ({
                url: `/ticket-service/v1/support-tickets/${id}/assign`,
                method: 'PUT',
            }),
            invalidatesTags: (result, error, { id }) => [
                { type: 'SupportTicketDetails', id: id },
                { type: 'SupportHistory', id: id },
                'SupportTickets',
                'SupportStats'
            ]
        }),

        closeSupportTicket: mutation<CloseSupportTicketResponse, { closeMsg: string, ticketId: string }>({
            query: ({ ticketId, closeMsg }) => ({
                url: `/ticket-service/v1/support-tickets/${ticketId}/close`,
                method: 'PUT',
                body: { closeMsg }
            }),
            invalidatesTags: (result, error, { ticketId }) => [
                { type: 'SupportTicketDetails', id: ticketId },
                { type: 'SupportHistory', id: ticketId },
                'SupportTickets',
                'SupportStats'
            ]

        }),

        createSupportTicketChat: mutation<CreateChatResponse, { id?: string, ticketId?: string }>({
            query: (params) => {
                const targetId = params.id || params.ticketId || '';
                return {
                    url: `/ticket-service/v1/support-tickets/${targetId}/create-chat`,
                    method: 'PUT',
                };
            },
            invalidatesTags: (result, error, params) => {
                const targetId = params.id || params.ticketId || '';
                return [
                    { type: 'SupportTicketDetails', id: targetId },
                    { type: 'SupportHistory', id: targetId },
                    'SupportTickets',
                    'SupportStats',
                ];
            }

        }),

        getSupportMemberTickets: query<GetSupportMemberTicketsResponse, GetSupportParams>({
            query: (params) => ({
                url: '/ticket-service/v1/support-tickets/report',
                method: 'GET',
                params: {
                    search: params.search || undefined,
                    page: params.page ?? 0,
                    limit: params.limit ?? 10,
                },
            }),
        }),

        getSupportChatHistory: query<GetSupportChatHistoryResponse, GetSupportChatHistoryParams>({
            query: ({ roomId, page = 0, limit = 20 }) => ({
                url: `/chat-service/v1/operator/chat/history/${roomId}`,
                method: 'GET',
                params: {
                    page,
                    limit,
                },
            }),
        }),


    }),
});

export const {
    useGetSupportStatsQuery,
    useGetSupportTicketsQuery,
    useGetSupportTicketDetailsQuery,
    useGetSupportTicketHistoryQuery,
    useCloseSupportTicketMutation,
    useCreateSupportTicketChatMutation,
    useAssignSupportTicketMutation,
    useGetSupportMemberTicketsQuery,
    useGetSupportChatHistoryQuery

} = SupportService;